import {
  DailyUsage,
  DailyUsageAPI,
  ProjectedDailyFlow,
  ProjectedMonthlyCost,
  Report30Days,
  Report30DaysAPI,
  ReportHourKey,
} from './model/report30Days.model'

// One list, two derived arrays, so the wire key and the display label cannot drift apart.
//
// The order here is the report's, midnight first. It is deliberately NOT the schema's
// property order, which lists midnight last — JSON property order carries no meaning, and
// building the keys from it would shift every column of the table by one.
const REPORT_HOURS: readonly (readonly [ReportHourKey, string])[] = [
  ['midnight', 'Midnight'],
  ['1am', '1am'],
  ['2am', '2am'],
  ['3am', '3am'],
  ['4am', '4am'],
  ['5am', '5am'],
  ['6am', '6am'],
  ['7am', '7am'],
  ['8am', '8am'],
  ['9am', '9am'],
  ['10am', '10am'],
  ['11am', '11am'],
  ['midday', 'Midday'],
  ['1pm', '1pm'],
  ['2pm', '2pm'],
  ['3pm', '3pm'],
  ['4pm', '4pm'],
  ['5pm', '5pm'],
  ['6pm', '6pm'],
  ['7pm', '7pm'],
  ['8pm', '8pm'],
  ['9pm', '9pm'],
  ['10pm', '10pm'],
  ['11pm', '11pm'],
]

export const REPORT_HOUR_KEYS: ReportHourKey[] = REPORT_HOURS.map(([key]) => key)
export const REPORT_TABLE_HEADERS: string[] = REPORT_HOURS.map(([, label]) => label)

// Distinguishable from a transport failure so the query can decline to retry it. A
// malformed payload is deterministic: repeating the request returns the same bytes and
// fails identically, so retrying only delays the error behind the backoff.
export class Report30DaysMappingError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'Report30DaysMappingError'
  }
}

const REQUIRED_COLLECTIONS = [
  'est30Day',
  'est7Day',
  'est12Month',
  'dailyUsage30Day',
] as const

const REQUIRED_SUMMARIES = [
  'summary30Day',
  'summary12Month',
] as const

// Every field read below is `required` in the schema, so guarding all of them is exactly
// as strict as the contract and cannot reject a valid response.
//
// Deliberately a throw rather than defaults: a report mapped from a malformed payload
// renders, charts, exports to PDF and CSV, and gets emailed to a customer. A report that
// throws does none of those. Absent hours are NOT guarded — they are legitimately optional.
const assertUsageData = (response: Report30DaysAPI) => {
  const usageData = response?.usageData

  if (!usageData) throw new Report30DaysMappingError('30-day report: response is missing usageData')

  for (const field of REQUIRED_COLLECTIONS) {
    if (!Array.isArray(usageData[field])) {
      throw new Report30DaysMappingError(`30-day report: usageData.${field} is missing or not an array`)
    }
  }

  for (const field of REQUIRED_SUMMARIES) {
    if (!usageData[field]) {
      throw new Report30DaysMappingError(`30-day report: usageData.${field} is missing`)
    }
  }

  if (typeof usageData.currency !== 'string') {
    throw new Report30DaysMappingError('30-day report: usageData.currency is missing')
  }

  return usageData
}

// An hour reports "no reading" three ways: the key is absent, its value is null, or its
// value is the '-' sentinel. All three occur in live responses. parseInt turns the first
// two into NaN, which satisfies `number` and then renders as the text "NaN" in the table,
// the CSV, and the PDF.
const parseHour = (value: string | null | undefined) => {
  if (value === undefined || value === null || value === '-') return null

  const parsed = parseInt(value, 10)

  return Number.isNaN(parsed) ? null : parsed
}

// parseInt, not parseFloat: the wire carries two decimals and the report has always shown
// them truncated. Widening to parseFloat would change every displayed hourly figure.
const mapDailyUsage = (usage: DailyUsageAPI): DailyUsage => ({
  date: usage.date,
  total: parseInt(usage.dailyUsage, 10),
  hours: REPORT_HOUR_KEYS.map((key) => parseHour(usage[key])),
})

export const mapReport30Days = (
  query: { deviceId: string },
  response: Report30DaysAPI,
): Report30Days => {
  const usageData = assertUsageData(response)

  return {
    // The response identifies no device, so this comes from the request that produced it.
    deviceId: query.deviceId,
    currency: usageData.currency,
    uom: response.uom,
    tableHeaders: REPORT_TABLE_HEADERS,
    actualUsage: usageData.dailyUsage30Day.map(mapDailyUsage),
    projected30Day: usageData.est30Day.map(
      (est): ProjectedDailyFlow => ({
        date: est.date,
        flow: est.flow,
        wasteEstimated: est.wasteEst,
        flowLessWasteEstimated: est.flowLessWasteEst,
        flowLessWasteEstimatedCost: est.flowLessWasteEstCost,
      }),
    ),
    // est7Day carries no flow or cost, so those two stay absent here.
    projected30DayHourly: usageData.est7Day.map(
      (est): ProjectedDailyFlow => ({
        date: est.date,
        wasteEstimated: est.wasteEst,
        flowLessWasteEstimated: est.flowLessWasteEst,
      }),
    ),
    projectedYearlyCost: usageData.est12Month.map(
      (est): ProjectedMonthlyCost => ({
        date: est.monthYear,
        cost: est.costWasteCum,
        costLessWaste: est.costLessWasteEstCum,
      }),
    ),
    stats: {
      monthlyCost: usageData.summary30Day.cost,
      monthlyWasteCost: usageData.summary30Day.wasteEstCost,
      monthlyWaste: usageData.summary30Day.flowLessWasteEst,
      monthlyFlow: usageData.summary30Day.flow,
      yearlyCost: usageData.summary12Month.cost,
      yearlyCostEstimated: usageData.summary12Month.wasteEstCost,
      yearlyEstimatedSaving: usageData.summary12Month.saving,
      yearlyFlow: usageData.summary12Month.flow,
      yearlyWaste: usageData.summary12Month.flowLessWasteEst,
      yearlySaving: usageData.summary12Month.flowSaving,
    },
  }
}
